import React from 'react'
import { formatDistanceToNowStrict } from 'date-fns'
import { Box, Button, Flex, Link, Loading, Message, Text } from '@workwhile/ui'
import { formatBillRateShiftDateTime } from '../formatBillRateShiftDateTime'
import { getBillRateShiftRowTone } from '../getBillRateShiftRowTone'
import {
  BILL_RATE_SHIFT_TIMING_LABEL,
  getBillRateShiftTiming,
} from '../getBillRateShiftTiming'
import { useBillRateShiftsContextValue } from '../context/BillRateShiftsProvider'

const BillRateShiftDateTime = ({
  date,
  timezone,
}: {
  date: string
  timezone: string | null
}) => {
  return (
    <Flex flexDirection="column">
      <Text>{formatBillRateShiftDateTime(date, timezone)}</Text>
      <Text color="lightText">
        ({formatDistanceToNowStrict(date, { addSuffix: true })})
      </Text>
    </Flex>
  )
}

export const BillRateShiftsMain = () => {
  const {
    state: {
      items,
      isLoading,
      isError,
      isFetchNextPageError,
      errorMessage,
      hasNextPage,
      isFetchingNextPage,
    },
    actions: { refetch, loadMore },
  } = useBillRateShiftsContextValue()

  if (isLoading) {
    return <Loading />
  }

  if (isError) {
    return (
      <Box>
        <Message variant="error">{errorMessage}</Message>
        <Button mt={2} onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    )
  }

  if (items.length === 0) {
    return <Box>No shifts associated with this bill rate.</Box>
  }

  return (
    <Box>
      <Box
        as="table"
        width="100%"
        border="1px solid"
        borderColor="neutrals.100"
        borderRadius="standard"
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <Box as="thead">
          <Box as="tr">
            {[
              'Shift ID',
              'Timing',
              'Shift start',
              'Shift end',
              'Workers',
              'Invoiced',
            ].map((heading) => (
              <Box
                as="th"
                key={heading}
                p={3}
                fontWeight={500}
                textAlign="left"
                color="lightText"
                backgroundColor="neutrals.100"
              >
                {heading}
              </Box>
            ))}
          </Box>
        </Box>
        <Box as="tbody">
          {items.map((shift) => {
            const timing = getBillRateShiftTiming(shift)
            const tone = getBillRateShiftRowTone(shift)
            const opacity = tone === 'full' ? 1 : tone === 'medium' ? 0.7 : 0.4
            const color = tone === 'dim' ? 'lightText' : undefined

            return (
              <Box
                as="tr"
                key={shift.id}
                data-testid="bill-rate-shift-row"
                data-tone={tone}
                data-timing={timing}
                opacity={opacity}
                color={color}
                backgroundColor={
                  timing === 'ongoing' ? 'rgba(220, 38, 38, 0.04)' : undefined
                }
              >
                <Box as="td" p={3} verticalAlign="top">
                  <Link to={`/shifts/${shift.id}`}>{shift.id}</Link>
                </Box>
                <Box as="td" p={3} verticalAlign="top">
                  {BILL_RATE_SHIFT_TIMING_LABEL[timing]}
                </Box>
                <Box as="td" p={3} verticalAlign="top">
                  <BillRateShiftDateTime
                    date={shift.startsAt}
                    timezone={shift.timezone}
                  />
                </Box>
                <Box as="td" p={3} verticalAlign="top">
                  <BillRateShiftDateTime
                    date={shift.endsAt}
                    timezone={shift.timezone}
                  />
                </Box>
                <Box as="td" p={3} verticalAlign="top">
                  {shift.workersAccepted} / {shift.workersNeeded}
                </Box>
                <Box as="td" p={3} verticalAlign="top">
                  {shift.invoiced ? 'Yes' : 'No'}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
      {hasNextPage && !isFetchNextPageError ? (
        <Flex mt={2} justifyContent="center">
          <Button
            variant="text"
            kind="small"
            loading={isFetchingNextPage}
            onClick={() => {
              void loadMore()
            }}
          >
            Load more
          </Button>
        </Flex>
      ) : null}
      {isFetchNextPageError && errorMessage ? (
        <Box mt={2}>
          <Message variant="error">{errorMessage}</Message>
          <Button
            mt={2}
            variant="text"
            kind="small"
            onClick={() => {
              void loadMore()
            }}
          >
            Retry
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
